import React, { useState, useEffect, useCallback, useContext } from "react";
import { GlobalTable, HeaderLine, Td, Th, Tr } from "../../../../components/styles/table-style";
import { PrioritySelect, Select, PrioritySelectContainer, SelectContainer, SelectArrow } from "./style";
import arrowDown from "../../../../assets/addStockModal/excel/arrow-down.svg";
import FashionApiService from '../../../../services/FashionApiService';
import ApiService from '../../../../services/ApiService';
import { FeedbackContext } from "../../../../contexts/FeedbackContext";

export interface FashionProductionItemsData {
    id: number;
    serviceId: number;
    fashionOrderId: number;
    quantityForThisProduction: number;
    fashionOrder?: {
        id: number;
        Company_Order_clientIdToCompany?: { companyName: string };
    };
}

export interface FashionProductionOrderData {
    id: number;
    set: string;
    status: number;
    dressmakerId?: number | null;
    cutterId?: number | null;
    collectionColorId?: number | null;
    productId?: number | null;
    priority?: string;
    machineId?: number | null;
    productionDay?: string;
    codeOfOP?: string;

    CollectionColor?: { id: number; name: string } | null;
    Product?: { id: number; name: string } | null;
    User_Services_cutterIdToUser?: { id: number; name: string } | null;
    User_Services_dressmakerIdToUser?: { id: number; name: string } | null;
    Machine?: { id: number; code: string; name: string } | null;

    FashionProductionItems?: FashionProductionItemsData[];

    totalItems?: number;
    materialsAvailable?: boolean | null;
}

interface Machine {
    id: number;
    code: string;
    name: string;
}

interface TableRowData {
    op: string;
    pedido: string;
    qtdItens: number;
    etapa: string;
    prioridade: string;
    machineId: number | null;
    dia: string;
    originalOrderId: number;
}

const PRIORIDADE_OPTIONS = ['Alta', 'Média', 'Baixa'];
const DIA_OPTIONS = ['Segunda', 'Terça', 'Quarta', 'Quinta', 'Sexta'];

const TablePCP: React.FC = () => {
    const { setFeedback } = useContext(FeedbackContext);
    const columns = ['OP', 'Pedido', 'Qtd. de itens', 'Etapa', 'Prioridade', 'Máquina', 'Dia'];

    const [tableData, setTableData] = useState<TableRowData[]>([]);
    const [machines, setMachines] = useState<Machine[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [machinesLoaded, setMachinesLoaded] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const fetchMachines = useCallback(async () => {
        try {
            const response = await ApiService.get('/equipment/equipment');
            setMachines(response.data);
        } catch (err: any) {
            console.error("Erro ao buscar máquinas:", err);
            setError("Não foi possível carregar as máquinas.");
        } finally {
            setMachinesLoaded(true);
        }
    }, []);

    const fetchAndProcessProductionOrders = useCallback(async () => {
        setIsLoading(true);
        setError(null);
        try {
            const response = await FashionApiService.getProductionOrders();
            const rawOrders: FashionProductionOrderData[] = response.data;

            const statusLabels: Record<number, string> = {
                0: 'Pendente',
                1: 'Iniciado',
                2: 'Concluído',
            };

            const formattedData: TableRowData[] = rawOrders.map(order => {
                const opNumber = `OP-${order.id}`;

                const pedidos = order.FashionProductionItems
                    ?.map(item => `PED-${item.fashionOrderId}`)
                    .join(', ');

                const totalItems = order.FashionProductionItems?.reduce((sum, item) => sum + item.quantityForThisProduction, 0) || 0;

                const initialPriority = order.priority && PRIORIDADE_OPTIONS.includes(order.priority) ? order.priority : '';
                const initialMachineId = order.machineId || null;
                const getDayString = (day: any): string => {
                    if (day === 1 || day === '1' || day === 'Segunda') return 'Segunda';
                    if (day === 2 || day === '2' || day === 'Terça') return 'Terça';
                    if (day === 3 || day === '3' || day === 'Quarta') return 'Quarta';
                    if (day === 4 || day === '4' || day === 'Quinta') return 'Quinta';
                    if (day === 5 || day === '5' || day === 'Sexta') return 'Sexta';
                    return '';
                };
                const initialDay = order.productionDay ? getDayString(order.productionDay) : '';


                return {
                    originalOrderId: order.id,
                    op: opNumber,
                    pedido: pedidos || 'N/A',
                    qtdItens: totalItems,
                    etapa: statusLabels[order.status] || String(order.status),
                    prioridade: initialPriority,
                    machineId: initialMachineId,
                    dia: initialDay,
                };
            });

            setTableData(formattedData);
        } catch (err: any) {
            console.error("Erro ao buscar ordens de produção:", err);
            setError("Não foi possível carregar as ordens de produção.");
        } finally {
            setIsLoading(false);
        }
    }, [machines]);

    useEffect(() => {
        fetchMachines();
    }, [fetchMachines]);

    useEffect(() => {
        if (machines.length > 0) {
            fetchAndProcessProductionOrders();
        }
    }, [machines, fetchAndProcessProductionOrders]);

    const handleSelectChange = async (
        rowIndex: number,
        field: keyof TableRowData,
        value: string
    ) => {
        const row = tableData[rowIndex];
        const orderId = row.originalOrderId;

        setTableData(prevData => {
            const newData = [...prevData];
            const updatedValue = field === 'machineId' ? (value === '' ? null : Number(value)) : value;
            const updatedRow = { ...newData[rowIndex], [field]: updatedValue };
            newData[rowIndex] = updatedRow;
            return newData;
        });

        const updateData: any = {};

        if (field === 'machineId') {
            updateData.machineId = value === '' ? null : Number(value);
        } else if (field === 'prioridade') {
            updateData.priority = value === '' ? null : value;
        } else if (field === 'dia') {
            updateData.productionDay = value === '' ? null : value;
        }

        try {
            await FashionApiService.updateProductionOrder(orderId, updateData);

            let fieldName = '';
            let newValue = '';

            if (field === 'machineId') {
                const machine = machines.find(m => m.id === Number(value));
                fieldName = 'Máquina';
                newValue = machine ? `${machine.code} - ${machine.name}` : 'Nenhuma';
            } else if (field === 'prioridade') {
                fieldName = 'Prioridade';
                newValue = value || 'Nenhuma';
            } else if (field === 'dia') {
                fieldName = 'Dia de produção';
                newValue = value || 'Nenhum';
            }

            console.log(`✅ SUCESSO: ${fieldName} da OP-${orderId} atualizada para ${newValue}`);
        } catch (err) {
            console.error(`Erro ao atualizar ${field} da OP ${orderId}:`, err);
            setFeedback({ isOpen: true, message: `Falha ao atualizar ${field} da OP-${orderId}.`, statusOk: false });
            fetchAndProcessProductionOrders();
        }
    };

    if (machinesLoaded && machines.length === 0) {
        return <p>Nenhum equipamento cadastrado.</p>;
    }

    if (isLoading) {
        return <p>Carregando ordens de produção...</p>;
    }

    if (error) {
        return <p style={{ color: 'red' }}>Erro: {error}</p>;
    }

    if (tableData.length === 0) {
        return <p>Nenhuma ordem de produção encontrada.</p>;
    }

    return (
        <GlobalTable>
            <thead>
                <HeaderLine>
                    {columns.map((item, index) => (
                        <Th key={index}>{item}</Th>
                    ))}
                </HeaderLine>
            </thead>
            <tbody>
                {tableData.map((rowData, rowIndex) => (
                    <Tr key={rowData.originalOrderId}>
                        <Td>{rowData.op}</Td>
                        <Td>{rowData.pedido}</Td>
                        <Td>{rowData.qtdItens}</Td>
                        <Td>{rowData.etapa}</Td>

                        <Td>
                            <PrioritySelectContainer>
                                <PrioritySelect
                                    value={rowData.prioridade}
                                    onChange={(e) => handleSelectChange(rowIndex, 'prioridade', e.target.value)}
                                >
                                    <option value="">Selecione...</option>
                                    {PRIORIDADE_OPTIONS.map(option => (
                                        <option key={option} value={option}>{option}</option>
                                    ))}
                                </PrioritySelect>
                                <SelectArrow src={arrowDown} alt="Seta para baixo" />
                            </PrioritySelectContainer>
                        </Td>

                        <Td>
                            <SelectContainer>
                                <Select
                                    value={rowData.machineId || ''}
                                    onChange={(e) => handleSelectChange(rowIndex, 'machineId', e.target.value)}
                                >
                                    <option value="">Selecione...</option>
                                    {machines.map(machine => (
                                        <option key={machine.id} value={machine.id}>
                                            {machine.code} - {machine.name}
                                        </option>
                                    ))}
                                </Select>
                                <SelectArrow src={arrowDown} alt="Seta para baixo" />
                            </SelectContainer>
                        </Td>

                        <Td>
                            <SelectContainer>
                                <Select
                                    value={rowData.dia}
                                    onChange={(e) => handleSelectChange(rowIndex, 'dia', e.target.value)}
                                >
                                    <option value="">Selecione...</option>
                                    {DIA_OPTIONS.map(option => (
                                        <option key={option} value={option}>{option}</option>
                                    ))}
                                </Select>
                                <SelectArrow src={arrowDown} alt="Seta para baixo" />
                            </SelectContainer>
                        </Td>
                    </Tr>
                ))}
            </tbody>
        </GlobalTable>
    );
};

export default TablePCP;
